import { useState, useEffect, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation } from "@apollo/client";
import FocusedPageLayout from "@/layouts/FocusedPageLayout";
import EmptyState from "@/components/EmptyState";
import ErrorBoundary from "@/components/ErrorBoundary";
import Button from "@/components/Button";
import Dialog from "@/components/Dialog";
import PageHeading from "@/components/PageHeading/PageHeading";
import SectionHeading from "@/components/SectionHeading";
import RadioCard from "@/components/Radio/RadioCard";
import TextField from "@/components/TextField";
import DetailsCard from "@/components/DetailsCard";
import { SelectOptions } from "@/components/DetailsCard/DetailsCard";
import { MetricGridSkeleton, SectionHeadingSkeleton, ObjectHeaderSkeleton } from "@/components/Skeleton";
import {
  MERGE_PURGE_PERSON_QUERY,
  MERGE_PURGE_NOTES_QUERY,
  MERGE_PURGE_ORDERS_QUERY,
  PEOPLE_PROCESS_MERGE_MUTATION,
  PEOPLE_PROCESS_PURGE_MUTATION,
  PEOPLE_PROCESS_AUTH0_MUTATION,
  type MergePurgeOrder
} from "./queries";
import useProcessTracker from "@/hooks/useProcessTracker";
import { useRequireCapability, DOMAIN, PERMISSION } from "@/hooks/useCapabilities";
import { ProcessType } from "@/providers/ProcessTrackerProvider/processTypes";
import { PURGE_MIN_COUNT, PURGE_MAX_COUNT, MERGE_PURGE_MIN_COUNT, MERGE_PURGE_MAX_COUNT } from "./constants";
import type { CRMNote } from "@/types";
import { NoteNotableValues, NotePriorityValues } from "@/types";
import { CREATE_NOTE_MUTATION, type CreateNoteResponse } from "@/features/global/components/NotesTable/mutations";
import { renderName } from "@/lib/personHelpers";
import { toFormat } from "@/lib/dateHelpers";
import { formattedAddressLines } from "@/lib/addressHelpers";
import { formatPhoneNumber } from "@/lib/phoneHelpers";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { formatStatus } from "@/features/person/utils/utils";
import { STAGES_MAP } from "@/pages/billing/_utils";
import { ObjectHeadingWrapper } from "@/features/person/components/PersonObjectHeading/PersonObjectHeading";
import { Link, useNavigate } from "@/router";
import type { MaterialNames } from "@/components/material_symbol_names";
import type { TagType } from "@/components/Tag/Tag";
import type { Person } from "@/types";

function getMembershipOptions(person: Person): SelectOptions[] {
  const primaryEmployment = person.employments?.find((e) => e.isPrimary) ?? person.employments?.[0];

  const displayTitle = primaryEmployment?.formattedTitle || primaryEmployment?.title;
  const employmentOverline = primaryEmployment?.jobClassification
    ? `${displayTitle} (${primaryEmployment.jobClassification})`
    : displayTitle;

  return [
    {
      iconName: "business_center",
      overline: employmentOverline ?? "Employment",
      headline: primaryEmployment?.facility?.name ?? <span className="opacity-20">--</span>,
      supportingText: primaryEmployment?.startedOn ? `Since ${toFormat(primaryEmployment.startedOn)}` : undefined
    },
    {
      iconName: "domain",
      overline: "Section",
      headline: person.primarySection?.name ?? <span className="opacity-20">--</span>
    },
    {
      iconName: "badge",
      overline: "Status",
      headline: person.latestProgram?.status ? (
        person.latestProgram.status.charAt(0) + person.latestProgram.status.slice(1).toLowerCase()
      ) : (
        <span className="opacity-20">--</span>
      )
    }
  ];
}

function getSuspensionHistoryOptions(person: Person): SelectOptions[] {
  const suspensions = [...(person.ccmsSuspensions ?? [])].sort(
    (a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime()
  );

  return suspensions.map((suspension) => {
    const reason = suspension.reasonDescription
      ? `${suspension.reasonDescription} (${suspension.code})`
      : suspension.code;
    const startDate = toFormat(suspension.startDate);
    const endDate = suspension.endDate ? toFormat(suspension.endDate) : "Present";

    return { iconName: "account_circle_off", headline: reason, supportingText: `${startDate} - ${endDate}` };
  });
}

function getDocumentsOptions(person: Person): SelectOptions[] {
  const documents = [...(person.legacyDocuments ?? [])].sort(
    (a, b) => new Date(b.createdAt).getDate() - new Date(a.createdAt).getDate()
  );

  return documents.map((document) => ({
    iconName: "attach_file",
    overline: document.documentType ?? undefined,
    headline: document.fileName ?? undefined,
    supportingText: document.createdAt ? toFormat(document.createdAt) : undefined
  }));
}

function getBioPhotoOption(person: Person): SelectOptions[] {
  if (!person.legacyBioPhoto) return [];

  return [
    {
      iconName: "photo_camera",
      overline: "Bio Photo",
      supportingText: person.legacyBioPhoto.createdAt ? toFormat(person.legacyBioPhoto.createdAt) : undefined
    }
  ];
}

function getEmail(person: Person): string | null {
  return person.email || null;
}

function getAuth0Summary(person: Person): string | null {
  const { auth0LoginCount: count, auth0LastLogin: last } = person;
  const hasCount = !!count;
  const hasLast = !!last;
  const loginCount = `${count} ${count === 1 ? "login" : "logins"}`;

  switch (true) {
    case hasCount && hasLast:
      return `${loginCount} · last ${toFormat(last ?? "")}`;
    case hasCount:
      return loginCount;
    case hasLast:
      return `Last login ${toFormat(last ?? "")}`;
    default:
      return null;
  }
}

function getPhone(person: Person, phoneType: "mobile" | "home"): string | null {
  const phone = person.phoneNumbers?.find((p) => p.phoneType === phoneType);
  return phone ? formatPhoneNumber(phone.phoneNumber) : null;
}

function getHomeAddress(person: Person): string | null {
  const home = person.addresses?.find((a) => a.addressType === "home");
  if (!home) return null;
  const lines = formattedAddressLines(home);
  return `${lines.line1}, ${lines.line2}`;
}

function getAlternateMailingAddress(person: Person): string | null {
  const secondary = person.addresses?.find((a) => a.addressType === "secondary");
  if (!secondary) return null;
  const lines = formattedAddressLines(secondary);
  return `${lines.line1}, ${lines.line2}`;
}

function getDateOfBirth(person: Person): string | null {
  const { day, month, year } = person.dob || {};
  if (!day || !month || !year) return null;
  const dateStr = `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
  return toFormat(dateStr);
}

function getMaskedSsn(person: Person): string | null {
  const last4 = person.ssn?.last4;
  if (!last4) return null;
  return `•••-••-${last4}`;
}

function getPersonalInfoOptions(person: Person): SelectOptions[] {
  const middleInitial = person.middleName ? person.middleName.charAt(0) : null;
  return [
    {
      iconName: "person",
      overline: "First Name",
      headline: person.firstName || <span className="opacity-20">--</span>
    },
    {
      iconName: "person",
      overline: "Middle Initial",
      headline: middleInitial || <span className="opacity-20">--</span>
    },
    { iconName: "person", overline: "Last Name", headline: person.lastName || <span className="opacity-20">--</span> },
    {
      iconName: "person",
      overline: "Name Suffix",
      headline: person.suffixName || <span className="opacity-20">--</span>
    },
    {
      iconName: "mail",
      overline: "Email Address",
      headline: getEmail(person) ?? <span className="opacity-20">--</span>,
      supportingText: getAuth0Summary(person)
    },
    {
      iconName: "phone_iphone",
      overline: "Mobile Phone Number",
      headline: getPhone(person, "mobile") ?? <span className="opacity-20">--</span>
    },
    {
      iconName: "call",
      overline: "Home Phone Number",
      headline: getPhone(person, "home") ?? <span className="opacity-20">--</span>
    },
    {
      iconName: "home",
      overline: "Home Address",
      headline: getHomeAddress(person) ?? <span className="opacity-20">--</span>,
      ...(person.addresses?.find((a) => a.addressType === "home")?.id === person.contactAddressId
        ? { tagText: "Contact Preference", tagType: "information" as const }
        : {})
    },
    {
      iconName: "markunread_mailbox",
      overline: "Alternate Mailing Address",
      headline: getAlternateMailingAddress(person) ?? <span className="opacity-20">--</span>,
      ...(person.addresses?.find((a) => a.addressType === "secondary")?.id === person.contactAddressId
        ? { tagText: "Contact Preference", tagType: "information" as const }
        : {})
    },
    {
      iconName: "cake",
      overline: "Date of Birth",
      headline: getDateOfBirth(person) || <span className="opacity-20">--</span>
    },
    {
      iconName: "lock",
      overline: "Social Security Number",
      headline: getMaskedSsn(person) || <span className="opacity-20">--</span>
    }
  ];
}

interface PersonalInfoField {
  key: string;
  label: string;
  iconName: MaterialNames;
  getValue: (person: Person) => string | null;
  getTag?: (person: Person) => { tagText: string; tagType: TagType } | null;
  getSupportingText?: (person: Person) => string | null;
}

const personalInfoFields: PersonalInfoField[] = [
  { key: "firstName", label: "First Name", iconName: "person", getValue: (p) => p.firstName || null },
  {
    key: "middleName",
    label: "Middle Initial",
    iconName: "person",
    getValue: (p) => (p.middleName ? p.middleName.charAt(0) : null)
  },
  { key: "lastName", label: "Last Name", iconName: "person", getValue: (p) => p.lastName || null },
  { key: "suffixName", label: "Name Suffix", iconName: "person", getValue: (p) => p.suffixName || null },
  {
    key: "email",
    label: "Email Address",
    iconName: "mail",
    getValue: (p) => getEmail(p),
    getSupportingText: (p) => getAuth0Summary(p)
  },
  {
    key: "mobilePhone",
    label: "Mobile Phone Number",
    iconName: "phone_iphone",
    getValue: (p) => getPhone(p, "mobile")
  },
  { key: "homePhone", label: "Home Phone Number", iconName: "call", getValue: (p) => getPhone(p, "home") },
  {
    key: "address",
    label: "Home Address",
    iconName: "home",
    getValue: (p) => getHomeAddress(p),
    getTag: (p) =>
      p.addresses?.find((a) => a.addressType === "home")?.id === p.contactAddressId
        ? { tagText: "Contact Preference", tagType: "information" }
        : null
  },
  {
    key: "altAddress",
    label: "Alternate Mailing Address",
    iconName: "markunread_mailbox",
    getValue: (p) => getAlternateMailingAddress(p),
    getTag: (p) =>
      p.addresses?.find((a) => a.addressType === "secondary")?.id === p.contactAddressId
        ? { tagText: "Contact Preference", tagType: "information" }
        : null
  },
  { key: "dob", label: "Date of Birth", iconName: "cake", getValue: (p) => getDateOfBirth(p) },
  { key: "ssn", label: "Social Security Number", iconName: "lock", getValue: (p) => getMaskedSsn(p) }
];

function getDefaultSelectionForField(
  field: PersonalInfoField,
  people: Person[],
  primaryPersonId: string | null
): string | null {
  const primary = people.find((p) => p.pgaId === primaryPersonId);
  if (primary && field.getValue(primary) != null) return primaryPersonId;

  const other = people.find((p) => p.pgaId !== primaryPersonId && field.getValue(p) != null);
  if (other?.pgaId) return other.pgaId;

  return primaryPersonId;
}

function getMatchingFields(people: Person[]): { matching: PersonalInfoField[]; differing: PersonalInfoField[] } {
  const matching: PersonalInfoField[] = [];
  const differing: PersonalInfoField[] = [];

  for (const field of personalInfoFields) {
    const values = people.map((p) => field.getValue(p));
    const allMatch = values.every((v) => v === values[0]);
    if (allMatch) {
      matching.push(field);
    } else {
      differing.push(field);
    }
  }

  return { matching, differing };
}

function getMembershipHistoryOptions(person: Person): SelectOptions[] {
  const bands = [...(person.bands ?? [])].sort(
    (a, b) => new Date(b.startedOn).getTime() - new Date(a.startedOn).getTime()
  );

  return bands.map((band) => {
    const program = band.programType?.name;
    const classification = band.classification?.name;
    const headline = classification ? `${program} (${classification})` : program;
    const startDate = toFormat(band.startedOn);
    const endDate = band.expiresOn ? toFormat(band.expiresOn) : "Present";

    return {
      iconName: "badge",
      overline: band.createdReason ? formatStatus(band.createdReason) : undefined,
      headline: headline ?? <span className="opacity-20">--</span>,
      supportingText: `${startDate} - ${endDate}`
    };
  });
}

function getNotesOptions(notes: CRMNote[]): SelectOptions[] {
  return notes.map((note) => ({
    iconName: "note",
    overline: note.displayTitle ?? note.title ?? undefined,
    headline: note.content || <span className="opacity-20">--</span>,
    supportingText: note.updatedAt ? toFormat(note.updatedAt) : note.createdAt ? toFormat(note.createdAt) : undefined
  }));
}

function getOrderHistoryOptions(orders: MergePurgeOrder[]): SelectOptions[] {
  return orders.map((order) => {
    const paidAmount = centsToDollarsFormatted(order.totalPaid) ?? "$0.00";
    const balance = centsToDollarsFormatted(order.balance) ?? "$0.00";
    const dueDate = order.dueDate ? toFormat(order.dueDate) : "N/A";
    const stageTag = STAGES_MAP[order.status];

    return {
      iconName: "receipt_long",
      overline: `Due Date: ${dueDate}`,
      headline: order.description || <span className="opacity-20">--</span>,
      supportingText: `Paid Amount: ${paidAmount}\nBalance: ${balance}`,
      tagText: stageTag?.label ?? order.status,
      tagType: stageTag?.color ?? "surface"
    };
  });
}

interface PersonQueryResult {
  data: Person | null;
  loading: boolean;
  error: Error | undefined;
}

function PersonQueryLoader({
  pgaId,
  onResult
}: {
  pgaId: string;
  onResult: (pgaId: string, result: PersonQueryResult) => void;
}) {
  const { data, loading, error } = useQuery(MERGE_PURGE_PERSON_QUERY, { variables: { pgaId } });

  useEffect(() => {
    onResult(pgaId, { data: data?.person ?? null, loading, error });
  }, [data, loading, error, pgaId, onResult]);

  return null;
}

interface NotesQueryResult {
  data: CRMNote[];
  loading: boolean;
  error: Error | undefined;
}

function NotesQueryLoader({
  pgaId,
  onResult
}: {
  pgaId: string;
  onResult: (pgaId: string, result: NotesQueryResult) => void;
}) {
  const { data, loading, error } = useQuery(MERGE_PURGE_NOTES_QUERY, { variables: { pgaId } });

  useEffect(() => {
    onResult(pgaId, { data: data?.personNotes?.nodes ?? [], loading, error });
  }, [data, loading, error, pgaId, onResult]);

  return null;
}

interface OrdersQueryResult {
  data: MergePurgeOrder[];
  loading: boolean;
  error: Error | undefined;
}

function OrdersQueryLoader({
  pgaId,
  onResult
}: {
  pgaId: string;
  onResult: (pgaId: string, result: OrdersQueryResult) => void;
}) {
  const { data, loading, error } = useQuery(MERGE_PURGE_ORDERS_QUERY, { variables: { query: pgaId } });

  useEffect(() => {
    onResult(pgaId, { data: data?.commerceOrders?.nodes ?? [], loading, error });
  }, [data, loading, error, pgaId, onResult]);

  return null;
}

const MERGE_PURGE_STEPS = [
  {
    id: "merge",
    label: "Merge records",
    processingLabel: "Merging records…",
    completedLabel: "Records merged",
    errorLabel: "Merge failed"
  },
  {
    id: "purge",
    label: "Purge duplicate record",
    processingLabel: "Purging duplicate…",
    completedLabel: "Duplicate purged",
    errorLabel: "Purge failed"
  },
  {
    id: "auth0",
    label: "Update Auth0 account",
    processingLabel: "Updating Auth0…",
    completedLabel: "Auth0 updated",
    errorLabel: "Auth0 update failed"
  }
];

const PURGE_ONLY_STEPS = [
  {
    id: "purge",
    label: "Purge person record",
    processingLabel: "Purging person…",
    completedLabel: "Person purged",
    errorLabel: "Purge failed"
  }
];

export default function PersonMergePurgePage() {
  useRequireCapability({ domain: DOMAIN.PEOPLE, permission: PERMISSION.manageMergePurge });
  const [searchParams] = useSearchParams();
  const action = searchParams.get("action") ?? "merge-purge";
  const isPurgeOnly = action === "purge";
  const ids = (searchParams.get("ids")?.split(",").filter(Boolean) ?? []).sort((a, b) => Number(a) - Number(b));

  const [results, setResults] = useState<Record<string, PersonQueryResult>>({});
  const [notesResults, setNotesResults] = useState<Record<string, NotesQueryResult>>({});
  const [ordersResults, setOrdersResults] = useState<Record<string, OrdersQueryResult>>({});

  const handleResult = useCallback((pgaId: string, result: PersonQueryResult) => {
    setResults((prev) => {
      const existing = prev[pgaId];
      if (
        existing &&
        existing.loading === result.loading &&
        existing.data === result.data &&
        existing.error === result.error
      ) {
        return prev;
      }
      return { ...prev, [pgaId]: result };
    });
  }, []);

  const handleNotesResult = useCallback((pgaId: string, result: NotesQueryResult) => {
    setNotesResults((prev) => {
      const existing = prev[pgaId];
      if (
        existing &&
        existing.loading === result.loading &&
        existing.data === result.data &&
        existing.error === result.error
      ) {
        return prev;
      }
      return { ...prev, [pgaId]: result };
    });
  }, []);

  const handleOrdersResult = useCallback((pgaId: string, result: OrdersQueryResult) => {
    setOrdersResults((prev) => {
      const existing = prev[pgaId];
      if (
        existing &&
        existing.loading === result.loading &&
        existing.data === result.data &&
        existing.error === result.error
      ) {
        return prev;
      }
      return { ...prev, [pgaId]: result };
    });
  }, []);

  const loading =
    (ids.length > 0 && ids.some((id) => !results[id] || results[id].loading)) ||
    (ids.length > 0 && ids.some((id) => !notesResults[id] || (notesResults[id].loading && !notesResults[id].error))) ||
    (ids.length > 0 && ids.some((id) => !ordersResults[id] || (ordersResults[id].loading && !ordersResults[id].error)));
  const error = Object.values(results).find((r) => r.error)?.error;
  const people = ids.map((id) => results[id]?.data).filter((p): p is Person => p != null);

  const [selectedPersonId, setSelectedPersonId] = useState<string | null>(ids[0] ?? null);
  const [selectedFirstName, setSelectedFirstName] = useState<string | null>(ids[0] ?? null);
  const [selectedMiddleName, setSelectedMiddleName] = useState<string | null>(ids[0] ?? null);
  const [selectedLastName, setSelectedLastName] = useState<string | null>(ids[0] ?? null);
  const [selectedSuffix, setSelectedSuffix] = useState<string | null>(ids[0] ?? null);
  const [selectedEmail, setSelectedEmail] = useState<string | null>(ids[0] ?? null);
  const [selectedMobilePhone, setSelectedMobilePhone] = useState<string | null>(ids[0] ?? null);
  const [selectedHomePhone, setSelectedHomePhone] = useState<string | null>(ids[0] ?? null);
  const [selectedAddress, setSelectedAddress] = useState<string | null>(ids[0] ?? null);
  const [selectedAltAddress, setSelectedAltAddress] = useState<string | null>(ids[0] ?? null);
  const [selectedDob, setSelectedDob] = useState<string | null>(ids[0] ?? null);
  const [selectedSsn, setSelectedSsn] = useState<string | null>(ids[0] ?? null);

  const fieldSelections: Record<string, { value: string | null; setter: (v: string | null) => void }> = {
    firstName: { value: selectedFirstName, setter: setSelectedFirstName },
    middleName: { value: selectedMiddleName, setter: setSelectedMiddleName },
    lastName: { value: selectedLastName, setter: setSelectedLastName },
    suffixName: { value: selectedSuffix, setter: setSelectedSuffix },
    email: { value: selectedEmail, setter: setSelectedEmail },
    mobilePhone: { value: selectedMobilePhone, setter: setSelectedMobilePhone },
    homePhone: { value: selectedHomePhone, setter: setSelectedHomePhone },
    address: { value: selectedAddress, setter: setSelectedAddress },
    altAddress: { value: selectedAltAddress, setter: setSelectedAltAddress },
    dob: { value: selectedDob, setter: setSelectedDob },
    ssn: { value: selectedSsn, setter: setSelectedSsn }
  };

  useEffect(() => {
    if (!selectedPersonId) return;
    setSelectedFirstName(selectedPersonId);
    setSelectedMiddleName(selectedPersonId);
    setSelectedLastName(selectedPersonId);
    setSelectedSuffix(selectedPersonId);
    setSelectedEmail(selectedPersonId);
    setSelectedMobilePhone(selectedPersonId);
    setSelectedHomePhone(selectedPersonId);
    setSelectedAddress(selectedPersonId);
    setSelectedAltAddress(selectedPersonId);
    setSelectedDob(selectedPersonId);
    setSelectedSsn(selectedPersonId);
  }, [selectedPersonId]);

  const hasAppliedNullAwareDefaultsRef = useRef(false);

  useEffect(() => {
    if (hasAppliedNullAwareDefaultsRef.current) return;
    if (loading) return;
    if (people.length !== ids.length) return;
    if (!selectedPersonId) return;

    const setters: Record<string, (v: string | null) => void> = {
      firstName: setSelectedFirstName,
      middleName: setSelectedMiddleName,
      lastName: setSelectedLastName,
      suffixName: setSelectedSuffix,
      email: setSelectedEmail,
      mobilePhone: setSelectedMobilePhone,
      homePhone: setSelectedHomePhone,
      address: setSelectedAddress,
      altAddress: setSelectedAltAddress,
      dob: setSelectedDob,
      ssn: setSelectedSsn
    };

    personalInfoFields.forEach((field) => {
      const defaultId = getDefaultSelectionForField(field, people, selectedPersonId);
      setters[field.key]?.(defaultId);
    });

    hasAppliedNullAwareDefaultsRef.current = true;
  }, [loading, people, ids.length, selectedPersonId]);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const navigate = useNavigate();
  const [mergePerson] = useMutation(PEOPLE_PROCESS_MERGE_MUTATION);
  const [purgePerson] = useMutation(PEOPLE_PROCESS_PURGE_MUTATION);
  const [processAuth0] = useMutation(PEOPLE_PROCESS_AUTH0_MUTATION);
  const { startProcess, updateStep } = useProcessTracker();

  const idsParam = searchParams.get("ids") ?? "";
  const [internalNote, setInternalNote] = useState("");
  const [createNote] = useMutation<CreateNoteResponse>(CREATE_NOTE_MUTATION);

  useEffect(() => {
    const allIds = idsParam.split(",").filter(Boolean);
    const mergedIds = allIds.filter((id) => id !== selectedPersonId);
    const label = mergedIds.length === 1 ? "Person" : "Persons";
    const verb = mergedIds.length === 1 ? "was" : "were";
    setInternalNote(
      `${label} ${mergedIds
        .map((id) => `record #${id}`)
        .join(", ")} ${verb} merged into this person record and then purged`
    );
  }, [selectedPersonId, idsParam]);

  async function runMergeStep(processId: string, sourcePersonId: string, targetPersonId: string): Promise<boolean> {
    const emailPerson = people.find((p) => p.pgaId === selectedEmail);
    const middleNamePerson = people.find((p) => p.pgaId === selectedMiddleName);
    const mobilePhonePerson = people.find((p) => p.pgaId === selectedMobilePhone);
    const homePhonePerson = people.find((p) => p.pgaId === selectedHomePhone);
    const addressPerson = people.find((p) => p.pgaId === selectedAddress);
    const altAddressPerson = people.find((p) => p.pgaId === selectedAltAddress);

    updateStep(processId, "merge", "processing");
    try {
      const mergeResult = await mergePerson({
        variables: {
          input: {
            sourcePersonId,
            targetPersonId,
            email: emailPerson?.email ?? "",
            middleName: middleNamePerson?.middleName ?? "",
            phoneId: mobilePhonePerson?.phoneNumbers?.find((p) => p.phoneType === "mobile")?.id ?? null,
            homePhoneId: homePhonePerson?.phoneNumbers?.find((p) => p.phoneType === "home")?.id ?? null,
            homeAddressId: addressPerson?.addresses?.find((a) => a.addressType === "home")?.id ?? null,
            secondaryAddressId: altAddressPerson?.addresses?.find((a) => a.addressType === "secondary")?.id ?? null
          }
        }
      });

      if (!mergeResult.data?.peopleProcessMerge?.success) {
        updateStep(processId, "merge", "error", mergeResult.data?.peopleProcessMerge?.message || "Merge failed");
        return false;
      }

      if (internalNote.trim()) {
        await createNote({
          variables: {
            input: {
              notableId: selectedPersonId,
              notableType: NoteNotableValues.PERSON,
              title: "Merge/Purge Process Run",
              content: internalNote.trim(),
              priority: NotePriorityValues.NONE
            }
          }
        });
      }

      updateStep(processId, "merge", "complete");
      return true;
    } catch (e) {
      updateStep(processId, "merge", "error", e instanceof Error ? e.message : "Merge failed");
      return false;
    }
  }

  async function runPurgeStep(processId: string, sourcePersonId: string, purgeOnly: boolean): Promise<boolean> {
    updateStep(processId, "purge", "processing");
    try {
      const purgeResult = await purgePerson({
        variables: { purgeOnly, sourcePersonId }
      });

      if (!purgeResult.data?.peopleProcessPurge?.success) {
        updateStep(processId, "purge", "error", purgeResult.data?.peopleProcessPurge?.message || "Purge failed");
        return false;
      }
      updateStep(processId, "purge", "complete");
      return true;
    } catch (e) {
      updateStep(processId, "purge", "error", e instanceof Error ? e.message : "Purge failed");
      return false;
    }
  }

  async function runAuth0Step(processId: string, sourcePersonId: string, targetPersonId: string): Promise<boolean> {
    updateStep(processId, "auth0", "processing");
    try {
      const auth0Result = await processAuth0({
        variables: { sourcePersonId, targetPersonId }
      });

      if (!auth0Result.data?.peopleProcessAuth0?.success) {
        updateStep(processId, "auth0", "error", auth0Result.data?.peopleProcessAuth0?.message || "Auth0 update failed");
        return false;
      }
      updateStep(processId, "auth0", "complete");
      return true;
    } catch (e) {
      updateStep(processId, "auth0", "error", e instanceof Error ? e.message : "Auth0 update failed");
      return false;
    }
  }

  const minRequired = isPurgeOnly ? PURGE_MIN_COUNT : MERGE_PURGE_MIN_COUNT;
  const maxAllowed = isPurgeOnly ? PURGE_MAX_COUNT : MERGE_PURGE_MAX_COUNT;
  if (ids.length < minRequired || ids.length > maxAllowed) {
    return (
      <FocusedPageLayout backLink={{ to: "/people" }}>
        <EmptyState
          iconName="error"
          title="Invalid Selection"
          caption={
            isPurgeOnly
              ? "Please select a person from the People table to purge."
              : "Please select at least 2 people from the People table to compare."
          }
          action={{ children: "Back to People", to: "/people" }}
        />
      </FocusedPageLayout>
    );
  }

  if (error) {
    return (
      <FocusedPageLayout backLink={{ to: "/people" }}>
        <ErrorBoundary
          title="Failed to Load People"
          message="An error occurred while loading person records. Please try again."
          image="default"
          action={
            <Link to="/people">
              <Button variant="filled">Back to People</Button>
            </Link>
          }
        />
      </FocusedPageLayout>
    );
  }

  return (
    <FocusedPageLayout
      backLink={{ to: "/people", title: "Cancel" }}
      stickyActions
      actions={<Button onClick={() => setConfirmOpen(true)}>Continue</Button>}
    >
      {ids.map((id) => (
        <PersonQueryLoader key={id} pgaId={id} onResult={handleResult} />
      ))}
      {ids.map((id) => (
        <NotesQueryLoader key={`notes-${id}`} pgaId={id} onResult={handleNotesResult} />
      ))}
      {ids.map((id) => (
        <OrdersQueryLoader key={`orders-${id}`} pgaId={id} onResult={handleOrdersResult} />
      ))}
      <div className="pt-6 sm:pt-4">
        <PageHeading title={isPurgeOnly ? "Purge Person Record" : "Merge/Purge Person Records"} />

        {loading ? (
          <>
            {isPurgeOnly ? (
              <ObjectHeaderSkeleton />
            ) : (
              <>
                <SectionHeadingSkeleton />
                <MetricGridSkeleton cards={ids.length} hasHeading />
              </>
            )}
            <SectionHeadingSkeleton />
            <MetricGridSkeleton cards={ids.length} hasHeading />
            <SectionHeadingSkeleton />
            <MetricGridSkeleton cards={ids.length} hasHeading />
            <SectionHeadingSkeleton />
            <MetricGridSkeleton cards={ids.length} hasHeading />
            <SectionHeadingSkeleton />
            <MetricGridSkeleton cards={ids.length} hasHeading />
            <SectionHeadingSkeleton />
            <MetricGridSkeleton cards={ids.length} hasHeading />
          </>
        ) : (
          <div
            className="grid gap-x-4 overflow-x-auto"
            style={{ gridTemplateColumns: `repeat(${people.length}, minmax(320px, 1fr))` }}
          >
            {isPurgeOnly ? (
              <div style={{ gridColumn: "1 / -1" }}>
                <ObjectHeadingWrapper pgaId={ids[0]} variant="condensed" avatarSize="large" />
              </div>
            ) : (
              <>
                <div style={{ gridColumn: "1 / -1" }}>
                  <SectionHeading
                    heading="Person Record to Keep"
                    body="Select the person record you want to keep as the final record. PGA Membership Services recommends keeping the record with the lower PGA ID. However, if another record has more data, it is acceptable to keep that record instead."
                  />
                </div>

                {people.map((person: Person) => {
                  const name = renderName(
                    person.displayName,
                    person.informalName,
                    person.preferredLastName,
                    person.suffixName,
                    undefined,
                    person.firstName
                  );
                  return (
                    <RadioCard
                      key={person.pgaId}
                      name="person-to-keep"
                      headline={name}
                      supportingText={`PGA ID: ${person.pgaId}`}
                      checked={selectedPersonId === person.pgaId}
                      onChange={() => setSelectedPersonId(person.pgaId ?? null)}
                      picture={person.profilePhoto}
                      useAvatar
                    />
                  );
                })}
              </>
            )}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Personal Information"
                body={
                  isPurgeOnly
                    ? "Existing personal information for this person. This data will be permanently deleted."
                    : "Select the personal information you want to keep in the final record for data items that are not the same. The non-selected data will be deleted."
                }
              />
            </div>

            {isPurgeOnly ? (
              people.map((person: Person) => (
                <DetailsCard key={`personal-${person.pgaId}`} options={getPersonalInfoOptions(person)} />
              ))
            ) : (
              <>
                {(() => {
                  const { matching, differing } = getMatchingFields(people);
                  return (
                    <>
                      {matching.length > 0 &&
                        people.map((person: Person) => (
                          <DetailsCard
                            key={`personal-match-${person.pgaId}`}
                            options={matching.map((field) => {
                              const tag = field.getTag?.(person);
                              const supportingText = field.getSupportingText?.(person);
                              return {
                                iconName: field.iconName,
                                overline: field.label,
                                headline: field.getValue(person) || <span className="opacity-20">--</span>,
                                ...(supportingText ? { supportingText } : {}),
                                ...(tag ? { tagText: tag.tagText, tagType: tag.tagType } : {})
                              };
                            })}
                          />
                        ))}

                      {matching.length > 0 && differing.length > 0 && (
                        <div style={{ gridColumn: "1 / -1" }} className="mt-6" />
                      )}

                      {differing.map((field, fieldIdx) => (
                        <div
                          key={field.key}
                          className={fieldIdx > 0 ? "mt-3" : ""}
                          style={{
                            gridColumn: "1 / -1",
                            display: "grid",
                            gridTemplateColumns: `repeat(${people.length}, minmax(320px, 1fr))`,
                            gap: "1rem"
                          }}
                        >
                          {people.map((person: Person) => {
                            const tag = field.getTag?.(person);
                            const extraSupportingText = field.getSupportingText?.(person);
                            const supportingText = extraSupportingText
                              ? `${field.label} · ${extraSupportingText}`
                              : field.label;
                            return (
                              <RadioCard
                                key={person.pgaId}
                                name={`${field.key}-to-keep`}
                                headline={field.getValue(person) || <span className="opacity-20">--</span>}
                                supportingText={supportingText}
                                checked={fieldSelections[field.key]?.value === person.pgaId}
                                onChange={() => fieldSelections[field.key]?.setter(person.pgaId ?? null)}
                                tagText={tag?.tagText}
                                tagType={tag?.tagType}
                              />
                            );
                          })}
                        </div>
                      ))}
                    </>
                  );
                })()}
              </>
            )}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Membership Details"
                body={
                  isPurgeOnly
                    ? "Existing membership details for this person. This data will be permanently deleted."
                    : "The existing membership details are shown below for reference only, the final record will be based on the membership status of the merged person record."
                }
              />
            </div>

            {people.map((person: Person) => (
              <DetailsCard key={`membership-${person.pgaId}`} options={getMembershipOptions(person)} />
            ))}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Suspension History"
                body={
                  isPurgeOnly
                    ? "Existing suspension history for this person. This data will be permanently deleted."
                    : "Existing suspension history for each person is shown below for reference. The final suspension records will be combined as appropriate."
                }
              />
            </div>

            {people.map((person: Person) => {
              const options = getSuspensionHistoryOptions(person);
              return options.length > 0 ? (
                <DetailsCard key={`suspensions-${person.pgaId}`} options={options} />
              ) : (
                <div
                  key={`suspensions-${person.pgaId}`}
                  className="flex items-center justify-center p-6 border border-sys-brand-outline rounded-lg opacity-50"
                >
                  No suspension history
                </div>
              );
            })}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Notes"
                body={
                  isPurgeOnly
                    ? "Existing notes for this person. These will be permanently deleted."
                    : "Existing notes for each person are shown below for reference. The final notes will be combined as appropriate."
                }
              />
            </div>

            {people.map((person: Person) => {
              const pgaId = person.pgaId ?? "";
              const notesResult = notesResults[pgaId];
              if (notesResult?.error) {
                return (
                  <ErrorBoundary
                    key={`notes-${pgaId}`}
                    variant="inline"
                    title="Failed to load notes"
                    message={notesResult.error.message}
                  />
                );
              }
              const options = getNotesOptions(notesResult?.data ?? []);
              return options.length > 0 ? (
                <DetailsCard key={`notes-${pgaId}`} options={options} />
              ) : (
                <div
                  key={`notes-${pgaId}`}
                  className="flex items-center justify-center p-6 border border-sys-brand-outline rounded-lg opacity-50"
                >
                  No notes
                </div>
              );
            })}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Membership History"
                body={
                  isPurgeOnly
                    ? "Existing membership history for this person. This data will be permanently deleted."
                    : "Existing membership history for each person is shown below for reference. The final membership bands will be combined as appropriate."
                }
              />
            </div>

            {people.map((person: Person) => {
              const options = getMembershipHistoryOptions(person);
              return options.length > 0 ? (
                <DetailsCard key={`history-${person.pgaId}`} options={options} />
              ) : (
                <div
                  key={`history-${person.pgaId}`}
                  className="flex items-center justify-center p-6 border border-sys-brand-outline rounded-lg opacity-50"
                >
                  No membership history
                </div>
              );
            })}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Billing Information"
                body={
                  isPurgeOnly
                    ? "Existing billing orders for this person. This data will be permanently deleted."
                    : "Existing billing orders for each person are shown below for reference. The final billing records will be combined as appropriate."
                }
              />
            </div>

            {people.map((person: Person) => {
              const pgaId = person.pgaId ?? "";
              const ordersResult = ordersResults[pgaId];
              if (ordersResult?.error) {
                return (
                  <ErrorBoundary
                    key={`orders-${pgaId}`}
                    variant="inline"
                    title="Failed to load orders"
                    message={ordersResult.error.message}
                  />
                );
              }
              const options = getOrderHistoryOptions(ordersResult?.data ?? []);
              return options.length > 0 ? (
                <DetailsCard key={`orders-${pgaId}`} options={options} />
              ) : (
                <div
                  key={`orders-${pgaId}`}
                  className="flex items-center justify-center p-6 border border-sys-brand-outline rounded-lg opacity-50"
                >
                  No billing orders
                </div>
              );
            })}

            <div style={{ gridColumn: "1 / -1" }}>
              <SectionHeading
                heading="Documents"
                body={
                  isPurgeOnly
                    ? "Existing CCMS documents for this person. This data will be permanently deleted."
                    : "Existing CCMS documents for each person are shown below for reference. The final documents will be combined as appropriate."
                }
              />
            </div>

            {people.map((person: Person) => {
              const options = [...getDocumentsOptions(person), ...getBioPhotoOption(person)];
              return options.length > 0 ? (
                <DetailsCard key={`documents-${person.pgaId}`} options={options} />
              ) : (
                <div
                  key={`documents-${person.pgaId}`}
                  className="flex items-center justify-center p-6 border border-sys-brand-outline rounded-lg opacity-50"
                >
                  No Documents
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Dialog
        headline={isPurgeOnly ? "Start Purge Process" : "Start Merge/Purge Process"}
        open={confirmOpen}
        onClosed={() => setConfirmOpen(false)}
        actions={
          <>
            <Button variant="text" onClick={() => setConfirmOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="filled"
              theme={isPurgeOnly ? "error" : "light"}
              icon={isPurgeOnly ? "delete" : undefined}
              onClick={async () => {
                if (isPurgeOnly) {
                  const processId = startProcess(ProcessType.MERGE_PURGE, "Purge Person Record", PURGE_ONLY_STEPS, {
                    completionAction: { label: "Back to People", to: "/people" },
                    errorAction: { label: "Back to People", to: "/people" }
                  });

                  setConfirmOpen(false);
                  navigate("/people");

                  await runPurgeStep(processId, people[0].id as string, true);
                } else {
                  const processId = startProcess(
                    ProcessType.MERGE_PURGE,
                    "Merge/Purge Person Records",
                    MERGE_PURGE_STEPS,
                    {
                      completionAction: { label: "View Person", to: `/people/${selectedPersonId}/person` },
                      errorAction: { label: "Back to People", to: "/people" }
                    }
                  );

                  setConfirmOpen(false);
                  navigate("/people");

                  const sourcePersonId = people.find((p) => p.pgaId !== selectedPersonId)?.id as string;
                  const targetPersonId = people.find((p) => p.pgaId === selectedPersonId)?.id as string;

                  if (!(await runMergeStep(processId, sourcePersonId, targetPersonId))) return;
                  if (!(await runPurgeStep(processId, sourcePersonId, false))) return;
                  await runAuth0Step(processId, sourcePersonId, targetPersonId);
                }
              }}
            >
              Start Process
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-3 text-body-medium">
          {isPurgeOnly ? (
            <>
              <p>This will permanently delete the selected person record and all associated data.</p>
              <p>
                You can continue using the system normally while this process runs. A progress tracker will keep you
                informed of each step.
              </p>
            </>
          ) : (
            <>
              <p>The following steps will be performed:</p>
              <ol className="list-decimal pl-5 flex flex-col gap-1">
                <li>Merge the selected person records</li>
                <li>Purge the duplicate record</li>
                <li>Update the Auth0 account</li>
              </ol>
              <p>
                You can continue using the system normally while this process runs. A progress tracker will keep you
                informed of each step.
              </p>
              <div className="flex flex-col gap-2 mt-3">
                <TextField
                  label="Internal Note"
                  value={internalNote}
                  onChangeText={setInternalNote}
                  rows={3}
                  type="textarea"
                />
              </div>
            </>
          )}
        </div>
      </Dialog>
    </FocusedPageLayout>
  );
}
